import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Plus, X } from 'lucide-react';
import { toast } from 'sonner';

import { stationNetworkApi } from '../../lib/stationNetworkApi';
import { messageErreur } from '../../lib/erreurs';
import { libelle } from '../../lib/libelles';
import { formatDateHeure, formatMontant } from '../../lib/formats';
import { Aide } from '../ui/aide';
import { Badge } from '../ui/badge';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Switch } from '../ui/switch';
import { StatutBadge } from '../ui/StatutBadge';
import { SearchableSelect } from '../ui/searchable-select';
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from '../ui/dialog';
import { stationOption } from './StationPicker';

/**
 * Changements de prix programmés (lot 034), dans l'écran Prix : un produit, un
 * nouveau prix, une date et une heure d'effet, plusieurs stations. Le prix ne
 * s'applique qu'à l'échéance ; le plafond national est contrôlé à ce moment-là
 * (et dès la programmation). Un poste ouvert à l'échéance demande le relevé
 * « Changement de prix » des pistolets du produit.
 */

const CELLULES =
  'w-full border-collapse text-sm [&_td]:border-b [&_td]:border-border/60 [&_td]:px-3 [&_td]:py-2 [&_th]:whitespace-nowrap [&_th]:border-b [&_th]:border-border [&_th]:px-3 [&_th]:py-1.5 [&_th]:text-xs [&_th]:font-semibold [&_th]:text-muted-foreground [&_tr:last-child_td]:border-0';

/** Une heure plus tard, au format d'un champ « date et heure » local. */
const dansUneHeure = () => {
  const date = new Date(Date.now() + 3600000);
  date.setSeconds(0, 0);
  const decalage = date.getTimezoneOffset() * 60000;
  return new Date(date.getTime() - decalage).toISOString().slice(0, 16);
};

const versIso = (local) => {
  if (!local) return undefined;
  const date = new Date(local);
  return Number.isNaN(date.getTime()) ? undefined : date.toISOString();
};

const Champ = ({ id, label, children }) => (
  <div className="grid gap-1.5">
    <label htmlFor={id} className="text-sm font-medium text-foreground">
      {label}
    </label>
    {children}
  </div>
);

/** « Kalaban, ACI 2000 +2 » : les premières stations, puis le reste en nombre. */
const resumeStations = (stations) => {
  const noms = (stations || []).map((station) => station.name).filter(Boolean);
  if (noms.length <= 2) return noms.join(', ') || '—';
  return `${noms.slice(0, 2).join(', ')} +${noms.length - 2}`;
};

// Stations qui n'ont pas reçu leur prix : refus définitif ou échec technique.
const refuses = (changement) => (changement.results || []).filter((ligne) => ligne.status && ligne.status !== 'APPLIED');

// --- Programmer ---------------------------------------------------------------------------

const ProgrammerDialog = ({ open, onOpenChange, produits, stations, stationInitiale, onSaved }) => {
  const [produitId, setProduitId] = useState('');
  const [prix, setPrix] = useState('');
  const [effet, setEffet] = useState(dansUneHeure());
  const [toutes, setToutes] = useState(false);
  const [choisies, setChoisies] = useState([]);
  const [erreur, setErreur] = useState('');
  const [enCours, setEnCours] = useState(false);

  useEffect(() => {
    if (!open) return;
    setProduitId(produits.find((ligne) => ligne.in_station)?.product_id || produits[0]?.product_id || '');
    setPrix('');
    setEffet(dansUneHeure());
    setToutes(false);
    setChoisies(stationInitiale ? [stationInitiale] : []);
    setErreur('');
  }, [open, produits, stationInitiale]);

  const optionsProduits = useMemo(
    () => produits.map((ligne) => ({ value: ligne.product_id, label: ligne.product_name })),
    [produits],
  );
  const optionsStations = useMemo(
    () => stations.filter((station) => !station.archived_at && !choisies.includes(station.id)).map(stationOption),
    [stations, choisies],
  );
  const nomStation = (id) => stations.find((station) => station.id === id)?.name || '';

  const ligne = produits.find((item) => item.product_id === produitId);
  const plafond = ligne?.ceiling?.ceiling_price;
  const auDessus = plafond !== undefined && plafond !== null && Number(prix) > Number(plafond);

  const programmer = async (event) => {
    event.preventDefault();
    setErreur('');
    setEnCours(true);
    try {
      const cree = await stationNetworkApi.priceChanges.schedule({
        productId: produitId,
        newPrice: Number(prix),
        effectiveAt: versIso(effet),
        stationIds: choisies,
        allStations: toutes,
      });
      toast.success('Changement de prix programmé');
      onSaved(cree);
      onOpenChange(false);
    } catch (probleme) {
      const message = messageErreur(probleme, 'Impossible de programmer le changement');
      setErreur(message);
      toast.error(message);
    } finally {
      setEnCours(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-1">
            Programmer un changement de prix
            <Aide titre="Prix programmé">
              Le prix s’applique à la date et à l’heure choisies, sur chaque station retenue. Le plafond national est
              contrôlé au moment de l’application.
            </Aide>
          </DialogTitle>
        </DialogHeader>
        <form onSubmit={programmer} className="grid gap-4 sm:grid-cols-2">
          <div className="sm:col-span-2">
            <Champ id="programme-produit" label="Produit">
              <SearchableSelect
                id="programme-produit"
                value={produitId}
                onValueChange={setProduitId}
                options={optionsProduits}
                aria-label="Produit"
                placeholder="Choisir le produit"
              />
            </Champ>
          </div>
          <Champ id="programme-prix" label="Nouveau prix du litre (FCFA)">
            <Input
              id="programme-prix"
              type="number"
              inputMode="numeric"
              min="1"
              step="1"
              value={prix}
              onChange={(event) => setPrix(event.target.value)}
              required
              aria-invalid={auDessus}
              className="text-right tabular-nums"
            />
          </Champ>
          <Champ id="programme-effet" label="À partir du">
            <Input id="programme-effet" type="datetime-local" value={effet} onChange={(event) => setEffet(event.target.value)} required />
          </Champ>
          {plafond !== undefined && plafond !== null && (
            <div className="flex flex-wrap gap-2 sm:col-span-2">
              <Badge variant={auDessus ? 'destructive' : 'muted'}>Plafond {formatMontant(plafond)}</Badge>
              {auDessus && <Badge variant="destructive">Au-dessus du plafond</Badge>}
            </div>
          )}
          <label className="flex items-center justify-between gap-3 rounded-lg border border-border p-3 text-sm sm:col-span-2">
            <span>Toutes les stations</span>
            <Switch checked={toutes} onCheckedChange={setToutes} aria-label="Toutes les stations" />
          </label>
          {!toutes && (
            <div className="grid gap-2 sm:col-span-2">
              <SearchableSelect
                value=""
                onValueChange={(id) => id && setChoisies((avant) => [...avant, id])}
                options={optionsStations}
                aria-label="Ajouter une station"
                placeholder="Ajouter une station"
                searchPlaceholder="Rechercher une station…"
                emptyMessage="Aucune station"
              />
              <div className="flex flex-wrap gap-1.5">
                {choisies.map((id) => (
                  <Badge key={id} variant="info" className="gap-1">
                    {nomStation(id)}
                    <button
                      type="button"
                      aria-label={`Retirer ${nomStation(id)}`}
                      onClick={() => setChoisies((avant) => avant.filter((valeur) => valeur !== id))}
                      className="rounded-sm hover:text-destructive focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                    >
                      <X className="h-3 w-3" aria-hidden="true" />
                    </button>
                  </Badge>
                ))}
              </div>
            </div>
          )}
          {erreur && (
            <p className="text-sm font-medium text-destructive sm:col-span-2" role="alert">
              {erreur}
            </p>
          )}
          <DialogFooter className="sm:col-span-2">
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
              Annuler
            </Button>
            <Button type="submit" disabled={enCours || !produitId || !prix || auDessus || (!toutes && choisies.length === 0)}>
              Programmer
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
};

// --- Panneau ---------------------------------------------------------------------------------

/**
 * @param stationId station courante de l'écran Prix
 * @param produits  lignes de `prices.active` (produit, plafond…) de la station courante
 * @param stations  toutes les stations
 */
const ScheduledPriceChanges = ({ stationId, produits, stations, peutEcrire, onChanged }) => {
  const [reseau, setReseau] = useState(false);
  const [changements, setChangements] = useState([]);
  const [dialogue, setDialogue] = useState(false);

  const charger = useCallback(async () => {
    try {
      const params = reseau || !stationId ? {} : { station_id: stationId };
      setChangements((await stationNetworkApi.priceChanges.list(params)) || []);
    } catch (erreur) {
      toast.error(messageErreur(erreur, 'Impossible de charger les prix programmés'));
    }
  }, [stationId, reseau]);

  useEffect(() => {
    charger();
  }, [charger]);

  const annuler = async (changement) => {
    if (!window.confirm('Annuler ce changement de prix programmé ?')) return;
    try {
      await stationNetworkApi.priceChanges.cancel(changement.id);
      toast.success('Changement annulé');
      await charger();
      onChanged?.();
    } catch (erreur) {
      toast.error(messageErreur(erreur, 'Impossible d’annuler le changement'));
    }
  };

  const apresProgrammation = async () => {
    await charger();
    onChanged?.();
  };

  return (
    <section className="rounded-xl border border-border bg-card shadow-sm">
      <header className="flex flex-wrap items-center justify-between gap-2 px-4 pb-2 pt-3">
        <h2 className="flex items-center gap-1 text-base font-semibold">
          Prix programmés
          <Aide titre="Prix programmés">
            Un changement de prix choisi à l’avance pour une ou plusieurs stations. Il s’applique à la date indiquée ;
            si le plafond national a baissé entre-temps, la station concernée est refusée.
          </Aide>
        </h2>
        <div className="flex flex-wrap items-center gap-3">
          <label className="flex items-center gap-2 text-xs text-muted-foreground">
            Tout le réseau
            <Switch checked={reseau} onCheckedChange={setReseau} aria-label="Tout le réseau" />
          </label>
          {peutEcrire && (
            <Button type="button" variant="outline" size="sm" onClick={() => setDialogue(true)}>
              <Plus className="h-4 w-4" aria-hidden="true" />
              Programmer
            </Button>
          )}
        </div>
      </header>
      <div className="overflow-x-auto px-1 pb-3">
        {changements.length === 0 ? (
          <p className="px-3 py-2 text-sm text-muted-foreground">Aucun prix programmé</p>
        ) : (
          <table className={CELLULES}>
            <thead>
              <tr>
                <th className="text-left">À partir du</th>
                <th className="text-left">Produit</th>
                <th className="text-right">Nouveau prix</th>
                <th className="text-left">Stations</th>
                <th className="text-left">État</th>
                <th aria-label="Actions" />
              </tr>
            </thead>
            <tbody>
              {changements.map((changement) => {
                const refus = refuses(changement);
                return (
                  <tr key={changement.id}>
                    <td className="whitespace-nowrap">{formatDateHeure(changement.effective_at)}</td>
                    <td>{changement.product_name || libelle('fuelProduct', changement.product_code, 'Produit à préciser')}</td>
                    <td className="text-right font-semibold tabular-nums">{formatMontant(changement.new_price)}</td>
                    <td title={(changement.stations || []).map((station) => station.name).join(', ')}>
                      {resumeStations(changement.stations)}
                    </td>
                    <td>
                      <div className="flex flex-wrap items-center gap-1.5">
                        <StatutBadge domaine="station_prix_programme" code={changement.status} />
                        {changement.status === 'APPLIED' && refus.length > 0 && (
                          <Badge variant="destructive" title={refus.map((ligne) => `${ligne.station_name} : ${ligne.message}`).join('\n')}>
                            {refus.length} sans prix
                          </Badge>
                        )}
                        {changement.status === 'APPLIED' &&
                          (changement.results || []).some((ligne) => ligne.requires_price_change_reading) && (
                            <Badge variant="warning">Relevé à faire</Badge>
                          )}
                        {changement.status === 'SCHEDULED' && changement.attempts > 0 && (
                          <Badge variant="warning">Nouvelle tentative (essai {changement.attempts})</Badge>
                        )}
                        {(changement.status === 'CANCELLED' || changement.status === 'FAILED') && changement.cancel_reason && (
                          <span className="text-xs text-muted-foreground">{changement.cancel_reason}</span>
                        )}
                      </div>
                    </td>
                    <td className="text-right">
                      {peutEcrire && changement.status === 'SCHEDULED' && (
                        <Button type="button" variant="ghost" size="sm" onClick={() => annuler(changement)}>
                          Annuler
                        </Button>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </div>
      <ProgrammerDialog
        open={dialogue}
        onOpenChange={setDialogue}
        produits={produits}
        stations={stations}
        stationInitiale={stationId}
        onSaved={apresProgrammation}
      />
    </section>
  );
};

export default ScheduledPriceChanges;
